import type { UIDataTypes, UIMessage } from 'ai';
import { z } from 'zod';
import type { AiArtifactSchema } from './ai-artifacts/list';

const fileSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
  type: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const onboardingInput = z.object({
  clientId: z.string().uuid().describe('The client id'),
  url: z.string().optional().describe('The url of the client'),
  files: z.array(fileSchema).optional().describe('The files of the client'),
});

export type OnboardingInput = z.infer<typeof onboardingInput>;

export const onboardingOutput = z.object({
  clientDescription: z.string().describe('The description of the client'),
  clientIndustry: z.string().describe('The industry of the client'),
  clientLocation: z.string().describe('The location of the client'),
  clientVoice: z.string().describe('The voice of the client'),
  clientTone: z.string().describe('The tone of the client'),
  clientPersonality: z.string().describe('The personality of the client'),
  clientTargetAudience: z.string().describe('The description of the target audience of the client'),
});

export type OnboardingOutput = z.infer<typeof onboardingOutput>;

export const onboardingToolInputSchema = z.object({
  prompt: z.string().describe('The prompt with the client information'),
});

export type OnboardingToolInput = z.infer<typeof onboardingToolInputSchema>;

export const colorSchemeSchema = z.object({
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  accentColors: z.array(z.string()).optional(),
});

export type ColorScheme = z.infer<typeof colorSchemeSchema>;

export const onboardingResult = z.object({
  clientDescription: z.string().describe('The description of the client'),
  clientIndustry: z.string().describe('The industry of the client'),
  clientLocation: z.string().describe('The location of the client'),
  clientVoice: z.string().describe('The voice of the client'),
  clientTone: z.string().describe('The tone of the client'),
  clientPersonality: z.string().describe('The personality of the client'),
  clientTargetAudience: z.string().describe('The description of the target audience of the client'),
  colorScheme: colorSchemeSchema,
});

export type OnboardingResult = z.infer<typeof onboardingResult>;

export type ImageSearchResult = {
  id: string;
  thumbnail: string;
  url: string;
  alt: string;
  photographer: string;
  photographer_url: string;
  width: number;
  height: number;
  download_url: string;
};

export enum UrlToolResultType {
  INSTAGRAM_POST = 'instagram_post',
  INSTAGRAM_USER = 'instagram_user',
  WEB_PAGE = 'web_page',
  NOT_FOUND = 'not_found',
  ERROR = 'error',
  TEXT_CONTENT = 'text_content',
  IMAGE = 'image',
  DOCUMENT = 'document',
  NOT_SUPPORTED = 'not_supported',
  INTERNAL_OBJECT = 'internal_object',
}

export type WebSearchResult = {
  title?: string;
  url: string;
  description: string;
  icon?: string;
};

export interface WebSearchClient {
  searchWeb(query: string, abortSignal?: AbortSignal): Promise<WebSearchResult[]>;
}

export type FileSearchResult = {
  id: string;
  name: string;
  mimeType?: string;
  size?: string;
  createdTime?: string;
  modifiedTime?: string;
  url?: string;
  snippet?: string;
};

export type FileReadResult = {
  id: string;
  name: string;
  mimeType?: string;
  size?: string;
  createdTime?: string;
  modifiedTime?: string;
  url?: string;
  snippet?: string;
  content?: string;
  contentLength?: number;
};

export interface FileSearchClient {
  searchFiles(
    baseUrl: string,
    query: string,
    abortSignal?: AbortSignal
  ): Promise<FileSearchResult[]>;
  getFileById(baseUrl: string, fileId: string): Promise<FileReadResult>;
}

export type ToolCallDetails = {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  result: Record<string, unknown> | string;
};

export type InstagramPostUrlToolResult = {
  type: UrlToolResultType.INSTAGRAM_POST;
  mediaType: 'image' | 'video';
  socialMediaPostId: string;
  caption?: string | null;
  mediaUrl?: string | null;
};

export type InstagramUserUrlToolResult = {
  type: UrlToolResultType.INSTAGRAM_USER;
  socialMediaUserId: string;
  username: string;
  profilePictureUrl?: string | null;
  biography?: string | null;
  url?: string | null;
};

export type WebPageUrlToolResult = {
  type: UrlToolResultType.WEB_PAGE;
  title: string;
  description: string;
  summary: string;
  url: string;
};

export type TextContentUrlToolResult = {
  type: UrlToolResultType.TEXT_CONTENT;
  summary: string;
};

export type ImageUrlToolResult = {
  type: UrlToolResultType.IMAGE;
  url: string;
};

export type DocumentUrlToolResult = {
  type: UrlToolResultType.DOCUMENT;
  url: string;
};

export type NotFoundUrlToolResult = {
  type: UrlToolResultType.NOT_FOUND;
};

export type ErrorUrlToolResult = {
  type: UrlToolResultType.ERROR;
  error: string;
};

export type NotSupportedUrlToolResult = {
  type: UrlToolResultType.NOT_SUPPORTED;
};

export type InternalObjectUrlToolResult = {
  type: UrlToolResultType.INTERNAL_OBJECT;
  content: string;
};

export type UrlToolResult =
  | InstagramPostUrlToolResult
  | InstagramUserUrlToolResult
  | WebPageUrlToolResult
  | TextContentUrlToolResult
  | ImageUrlToolResult
  | DocumentUrlToolResult
  | NotFoundUrlToolResult
  | ErrorUrlToolResult
  | NotSupportedUrlToolResult
  | InternalObjectUrlToolResult
  | InternalObjectUrlToolResult;

export const internalSearchToolInputSchema = z.object({
  query: z.string().describe('The search term. Only use a single word or expression at a time.'),
});

export type InternalSearchToolType = {
  input: z.infer<typeof internalSearchToolInputSchema>;
  output: string;
};

export const tasksSearchToolInputSchema = z.object({
  query: z
    .string()
    .describe(
      'The search term to filter tasks titles. Only use a single word or expression at a time.'
    )
    .optional(),
  dueDateFrom: z
    .string()
    .describe('The date from which the tasks are due. Use YYYY-MM-DD format.')
    .optional(),
  dueDateTo: z
    .string()
    .describe('The date to which the tasks are due. Use YYYY-MM-DD format.')
    .optional(),
  userId: z.string().describe('The id of the user to search tasks for.').optional(),
});

export type TasksSearchToolType = {
  input: z.infer<typeof tasksSearchToolInputSchema>;
  output: string;
};

export const addMemoryToolInputSchema = z.object({
  content: z.string().max(8000).describe('The content of the memory'),
});

export type AddMemoryToolType = {
  input: z.infer<typeof addMemoryToolInputSchema>;
  output: void;
};

export const saveMemoryToolInputSchema = z.object({
  type: z
    .string()
    .max(120)
    .describe(
      'Memory category: e.g. note, preference (tone/platform/style), workflow (successful reusable task pattern)'
    ),
  content: z.string().max(8000).describe('Searchable text for retrieval'),
  payload: z.record(z.unknown()).optional().describe('Structured JSON metadata'),
  clientId: z.string().uuid().optional().describe('Optional client scope'),
});

export type SaveMemoryToolType = {
  input: z.infer<typeof saveMemoryToolInputSchema>;
  output: string;
};

export const recallMemoryToolInputSchema = z.object({
  query: z.string().max(2000).describe('What to look for in saved memories'),
  clientId: z
    .string()
    .uuid()
    .optional()
    .describe('Optional: limit to this client and global memories'),
  limit: z.number().int().min(1).max(20).optional().describe('Max results (default 5)'),
});

export type RecallMemoryToolType = {
  input: z.infer<typeof recallMemoryToolInputSchema>;
  output: string;
};

export const deleteMemoryToolInputSchema = z.object({
  id: z.string().describe('The id of the memory to delete'),
});

export type DeleteMemoryToolType = {
  input: z.infer<typeof deleteMemoryToolInputSchema>;
  output: void;
};

export const webSearchToolInputSchema = z.object({
  query: z.string().describe('The search term to look up'),
});

export type WebSearchToolType = {
  input: z.infer<typeof webSearchToolInputSchema>;
  output: string;
};

export const imageToolInputSchema = z.object({
  prompt: z.string().describe('The prompt to generate an image from'),
});

export type ImageToolType = {
  input: z.infer<typeof imageToolInputSchema>;
  output: { artifacts: (AiArtifactSchema | null)[] };
};

export const imageSearchToolInputSchema = z.object({
  query: z.string().describe('The search term for finding images'),
});

export type ImageSearchToolType = {
  input: z.infer<typeof imageSearchToolInputSchema>;
  output: ImageSearchResult[];
};

export const urlToolInputSchema = z.object({
  url: z.string().describe('URL to retrieve the content of'),
});

export type UrlToolType = {
  input: z.infer<typeof urlToolInputSchema>;
  output: UrlToolResult;
};

export const createPostToolInputSchema = z.object({
  prompt: z.string().describe('The prompt for the post'),
});

export const createPostOutput = z.object({
  clientId: z.string().uuid().optional().describe('The client id'),
  theme: z.string().describe('A short description of the theme of the post'),
  content: z.string().describe('The content of the post'),
  visualInstructions: z
    .string()
    .describe(
      'The instructions to be handed over to a designer to create the visual content for the post'
    ),
  goal: z.string().describe('The goal of the post. What do you want the post to achieve?'),
});

export type CreatePostOutput = z.infer<typeof createPostOutput>;

export type CreatePostToolType = {
  input: z.infer<typeof createPostToolInputSchema>;
  output: CreatePostOutput;
};

export const plannerToolInputSchema = z.object({
  prompt: z.string().describe('The prompt for the planner'),
});

export const plannerOutput = z.object({
  posts: z.array(
    z.object({
      date: z.string().describe('The date of the post'),
      theme: z.string().describe('A short description of the theme of the post'),
      content: z.string().describe('The content of the post'),
      visualInstructions: z
        .string()
        .describe(
          'The instructions to be handed over to a designer to create the visual content for the post'
        ),
      goal: z.string().describe('The goal of the post. What do you want the post to achieve?'),
    })
  ),
});

export type PlannerOutput = z.infer<typeof plannerOutput>;

export type PlannerToolType = {
  input: z.infer<typeof plannerToolInputSchema>;
  output: PlannerOutput;
};

export const suggestContentToolInputSchema = z.object({
  content: z.string().describe('The full proposed Instagram caption text'),
});

export type SuggestContentToolType = {
  input: z.infer<typeof suggestContentToolInputSchema>;
  output: string;
};

export const reportToolInputSchema = z.object({
  prompt: z
    .string()
    .max(2000)
    .describe(
      'Plain-language description of the report to generate, e.g. "posts published per client last month" or "open tasks assigned to jane@agency.com"'
    ),
});

export type ReportToolType = {
  input: z.infer<typeof reportToolInputSchema>;
  output: { artifacts: (AiArtifactSchema | null)[]; error?: string };
};

export const viewFileToolInputSchema = z.object({
  fileId: z.string().describe('The file ID of the attachment to view'),
  prompt: z.string().describe('What to extract or interpret from this file'),
});

export type ViewFileToolType = {
  input: z.infer<typeof viewFileToolInputSchema>;
  output: string;
};

export const githubSearchToolInputSchema = z.object({
  query: z.string().describe('The search term to search in GitHub'),
});

export const githubSearchOutput = z.object({
  error: z.string().describe('The error message').optional(),
  files: z.array(
    z.object({
      name: z.string().describe('The name of the file'),
      path: z.string().describe('The path of the file'),
      matches: z.array(z.string()).describe('The matches in the file'),
    })
  ),
});

export type GitHubSearchOutput = z.infer<typeof githubSearchOutput>;

export type GitHubSearchToolType = {
  input: z.infer<typeof githubSearchToolInputSchema>;
  output: GitHubSearchOutput;
};

export const githubReadToolInputSchema = z.object({
  filePath: z.string().describe('The path of the file to read'),
});

export const githubReadOutput = z.object({
  error: z.string().describe('The error message').optional(),
  content: z.string().describe('The content of the file'),
});

export type GitHubReadOutput = z.infer<typeof githubReadOutput>;

export type GitHubReadToolType = {
  input: z.infer<typeof githubReadToolInputSchema>;
  output: GitHubReadOutput;
};

export type AmetistaUITools = {
  getInternalInformation: InternalSearchToolType;
  /** @deprecated Prefer saveMemory */
  addMemory: AddMemoryToolType;
  saveMemory: SaveMemoryToolType;
  recallMemory: RecallMemoryToolType;
  deleteMemory: DeleteMemoryToolType;
  webSearch: WebSearchToolType;
  generateImage: ImageToolType;
  imageSearch: ImageSearchToolType;
  url: UrlToolType;
  createPost: CreatePostToolType;
  planner: PlannerToolType;
  suggestContent: SuggestContentToolType;
  viewFile: ViewFileToolType;
  generateReport: ReportToolType;
};

export type AmetistaUIMessage = UIMessage<unknown, UIDataTypes, AmetistaUITools>;
